import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PortalPopup from "../../components/PortalPopup";
import EmailInputField from "../../components/EmailInputField";
import PasswordInputFieldRegister from "../../components/PasswordInputFieldRegister";
import CTAs from "../../components/CTAs";
import NameInput from "../../components/NameInput";
import { lawyerService } from "../../services/lawyerService";
import PlanSelected from "../../components/PlanSelected";
import Footer from "../../components/Footer";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [companyname, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedLawFirm, setSelectedLawFirm] = useState("");
  const [lawFirm, setLawFirm] = useState([]);
  const [isPlanSelectedOpen, setPlanSelectedOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (location.state?.selectedBlock) {
        setSelectedBlock(location.state.selectedBlock);
      }

      try {
        const response = await lawyerService.getLawyerDetails();
        const files = response?.files;
        console.log("full response:", JSON.stringify(response, null, 2));

        if (Array.isArray(files)) {
          const processed = files.map((item) => {
            const postcodeMatch = item.postcode?.match(
              /[A-Z]{1,2}\d{1,2} ?\d[A-Z]{2}/i,
            );
            const postcodeOnly = postcodeMatch ? postcodeMatch[0] : "";

            return {
              ...item,
              label: item.name?.toUpperCase() || "NO NAME",
              postcodeOnly,
            };
          });

          setLawFirm(processed);
        } else {
          console.error("Expected an array in response.files, got:", files);
        }
      } catch (error) {
        console.error("Error fetching lawyer details:", error);
      }
    };

    fetchData();
  }, [location.state]);

  const handleNameChange = (event) => setName(event.target.value);
  const handleCompanyNameChange = (event) => setCompanyName(event.target.value);
  const handleEmailChange = (value) => setEmail(value);
  const handlePasswordChange = (value) => setPassword(value);

  const handleLawFirmChange = (event) => {
    setSelectedLawFirm(event.target.value);
    const selected = lawFirm.find((firm) => firm.name === event.target.value);
    if (selected) {
      setEmail(selected.email);
    }
  };

  const openPlanSelected = useCallback(() => {
    if (!name.trim()) {
      alert("Please enter your full name");
      return;
    }
    if (!companyname.trim()) {
      alert("Please enter your company name");
      return;
    }
    if (!email.trim()) {
      alert("Please enter your email");
      return;
    }
    if (!password.trim()) {
      alert("Please enter your password");
      return;
    }
    if (selectedBlock === "block2" && !selectedLawFirm) {
      alert("Please select a law firm");
      return;
    }

    const data = {
      name,
      companyName: companyname,
      email,
      password,
      selectedBlock: selectedBlock,
    };

    if (selectedBlock === "block2") {
      const selectedFirm = lawFirm.find(
        (firm) => firm.name === selectedLawFirm,
      );
      data.email = selectedFirm ? selectedFirm.email : email;
      data.lawFirmName = selectedLawFirm;
      data.lawFirmEmail = selectedFirm ? selectedFirm.email : email;
      data.lawFirmPostcode = selectedFirm?.postcodeOnly || "";
    }

    setUserData(data);
    setPlanSelectedOpen(true);
  }, [
    name,
    companyname,
    email,
    password,
    selectedBlock,
    selectedLawFirm,
    lawFirm,
  ]);

  const closePlanSelected = useCallback(() => {
    setPlanSelectedOpen(false);
  }, []);

  const onLogInTextClick = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

        html, body {
          margin: 0;
          background: #ffffff !important;
        }

        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: #ffffff;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
        }

        .dd-form-container {
          width: 400px;
          max-width: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin: auto;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 6px;
          display: block;
        }

        .dd-required { color: #EF4444; margin-left: 2px; }

        .dd-input-group { display: flex; flex-direction: column; gap: 6px; }

        .dd-input {
          width: 100%;
          box-sizing: border-box;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }

        .dd-link {
          font-size: 13px; font-weight: 600; color: #2563EB;
          text-decoration: none; cursor: pointer;
        }
        .dd-link:hover { text-decoration: underline; }
        .dd-text { font-size: 13px; color: #64748B; }

        .dd-select-wrapper {
          position: relative;
          width: 100%;
        }
        .dd-select {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          appearance: none;
          transition: border-color 0.15s;
        }
        .dd-select:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .dd-select-arrow {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: #64748B;
        }
      `}</style>

      <div className="dd-root">
        <div className="dd-form-container">
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <img
              src="assets/main-logo.svg"
              alt="Logo"
              style={{ height: "40px" }}
            />
          </div>

          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#0F172A",
                margin: "0 0 8px 0",
              }}
            >
              Create an account
            </h1>
            <p className="dd-text">Register to your account</p>
          </div>

          {/* Name Input Fields */}
          <NameInput
            title="Full name"
            placeholder="John Doe"
            value={name}
            onChange={handleNameChange}
            width="100%"
            validationMessage="Please provide your full name."
            iconColor="#D93C3D"
            iconSize="20px"
            bgColorDark="#0C111D"
            bgColorLight="#FFFFFF"
            isDarkMode={isDarkMode}
          />

          <NameInput
            title="Company name"
            placeholder="6EL∆"
            value={companyname}
            onChange={handleCompanyNameChange}
            width="100%"
            validationMessage="Please provide your company name."
            iconColor="#D93C3D"
            iconSize="20px"
            bgColorDark="#0C111D"
            bgColorLight="#FFFFFF"
            isDarkMode={isDarkMode}
          />

          {/* Law Firm Select (only for professional services) */}
          {selectedBlock === "block2" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label className="dd-section-label">
                Select Law Firm <span className="dd-required">*</span>
              </label>
              <div className="dd-select-wrapper">
                <select
                  className="dd-select"
                  value={selectedLawFirm}
                  onChange={handleLawFirmChange}
                  required
                >
                  <option value="" disabled>
                    Select a law firm
                  </option>
                  {lawFirm.map((firm, index) => (
                    <option key={index} value={firm.name}>
                      {firm.name} {firm.postcodeOnly}
                    </option>
                  ))}
                </select>
                <div className="dd-select-arrow">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Email */}
          <EmailInputField
            title="Email"
            placeholder="name@yourcompany.com"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            width="100%"
          />

          {/* Password */}
          <PasswordInputFieldRegister
            title="Password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            width="100%"
            validationMessage="Please provide your password."
          />

          {/* Continue button */}
          <CTAs
            type="Signinbtn"
            text="Continue"
            width="100%"
            onClick={openPlanSelected}
          />

          {/* Already have an account? */}
          <div style={{ textAlign: "center" }}>
            <span className="dd-text">Already have an account? </span>
            <span className="dd-link" onClick={onLogInTextClick}>
              Log in
            </span>
          </div>
        </div>

        <Footer />
      </div>

      {isPlanSelectedOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closePlanSelected}
        >
          <PlanSelected onClose={closePlanSelected} userData={userData} />
        </PortalPopup>
      )}
    </>
  );
};

export default RegisterPage;
